import { AccountColumns } from '@juvare/contact-management/backend/features/accounts/shared';
import { AddressSchema } from '@juvare/contact-management/backend/features/accounts/shared/Address';
import { PersonCredentialsColumns } from '@juvare/credentialing/backend/features/person-credentials';
import { personCredentialsSchema } from '@juvare/credentialing/backend/features/person-credentials/entities/types';
import { EntityDef } from '@juvare/platform-app/App';
import { AppState, UserState } from '@juvare/platform-sdk-react/types';
import { EntityColumns, defineColumns } from '@juvare/platform-shared/features/storage';
import { adjustZodSchemaDefaultForUnknownFields, zodKnownFieldDefaultValidation } from '@juvare/platform-shared/features/validation';
import { z } from 'zod';
import { BackgroundCheckColumns, accountBackgroundCheckSchema } from '../../../background-check';

export const extendedAccountEntityName = 'extendedAccount';

const extendedAddressSchema = AddressSchema.merge(
    z.object({
        tribeId: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        county: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        residenceType: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
    }),
);
export type ExtendedAddress = z.infer<typeof extendedAddressSchema>;

const medicalHistorySchema = z.object({
    medicalHistoryMedicalPreparedness: z
        .object({
            areYouPhysicallyAbleToParticipateInAFieldDeployment: z.boolean().optional(),
            doYouHaveAnyHealthConditionsWhichMayPreventYouFromDeploymentInADisasterSituation: z.boolean().optional(),
            doYouHaveAnyHealthConditionsWhichMayPreventYouFromDeploymentInADisasterSituationDetails: z
                .string()
                .max(zodKnownFieldDefaultValidation.stringMax)
                .optional(),
            areYouAbleToClimbAtLeast3FlightsOfStairs: z.boolean().optional(),
            areYouAbleToStandFor2HourDurations: z.boolean().optional(),
            areYouAbleToSitFor2OrMoreHourDurations: z.boolean().optional(),
            areYouWillingToWorkA12HourDayUnderExtremeConditions: z.boolean().optional(),
            areYouAbleToLift25PoundsRepeatedly: z.boolean().optional(),
        })
        .optional(),
    medicalHistorySpecialNeeds: z
        .object({
            doYouHaveAnyRelevantDisabilitiesAndOrSpecialNeeds: z.boolean().optional(),
            doYouHaveAnyRelevantDisabilitiesAndOrSpecialNeedsDetails: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
            areYouCurrentlyTakingAnyMedicationsThatRequireRefrigeration: z.boolean().optional(),
            areYouCurrentlyTakingAnyMedicationsThatRequireRefrigerationDetails: z
                .string()
                .max(zodKnownFieldDefaultValidation.stringMax)
                .optional(),
            doYouNeedAmericansWithDisabilityActAdaAccommodations: z.boolean().optional(),
            ifYesListNeededAdaAccommodationsDetails: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
            doYouHaveAnyDietaryRestrictions: z.boolean().optional(),
            doYouHaveAnyDietaryRestrictionsDetails: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryAllergies: z
        .object({
            doYouHaveAnyKnownSeriousAllergies: z.boolean().optional(),
            doYouHaveAnyKnownSeriousAllergiesDetails: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryPsychologicalTraining: z
        .object({
            haveYouReceivedTrainingOnThePsychologicalImpactOfEmergencyResponse: z.boolean().optional(),
            haveYouReceivedTrainingOnThePsychologicalImpactOfEmergencyResponseDetails: z.string().date().optional(),
        })
        .optional(),
    medicalHistoryHepatitisA: z
        .object({
            dose1: z.string().date().optional(),
            dose2: z.string().date().optional(),
            additionalHepatitisAInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryHepatitisB: z
        .object({
            hepatitisBDose1: z.string().date().optional(),
            hepatitisBDose2: z.string().date().optional(),
            hepatitisBDose3: z.string().date().optional(),
            hepatitisBTiter: z.enum(['Positive', 'Negative', 'Not Performed']).optional(),
            additionalHepatitisBInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryTetanusDiphtheriaTd: z
        .object({
            tdMostRecentDose: z.string().date().optional(),
            additionalTdInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryMeaslesMumpsRubellaMmr: z
        .object({
            mmrDose1: z.string().date().optional(),
            mmrDose2: z.string().date().optional(),
            measlesTiter: z.string().date().optional(),
            mumpsTiter: z.string().date().optional(),
            rubellaTiter: z.string().date().optional(),
            haveYouEverHadACaseOfMeaslesMumpsRubellaMmr: z.enum(['Yes', 'No']).optional(),
            additionalMmrInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryVacciniaSmallpox: z
        .object({
            smallpoxDose1: z.string().date().optional(),
            smallpoxWasThereAMajorTakeResponse: z.enum(['Yes', 'No']).optional(),
            additionalSmallpoxInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryVaccineLiveAttenuatedInfluenzaVaccineLaiv: z
        .object({
            laivMostRecentDose: z.string().date().optional(),
            laivWhichVaccineDidYouReceive: z.enum(['Inactivated', 'Live']).optional(),
            additionalInfluenzaInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryTuberculosisTesting: z
        .object({
            tuberculosisMostRecentDose: z.string().date().optional(),
            resultOfPpd: z.enum(['Positive', 'Negative']).optional(),
            additionalTuberculosisInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryInactivatedPolioVaccineIpvOrOralPolioVaccineOpv: z
        .object({
            opvDose1: z.string().date().optional(),
            opvDose2: z.string().date().optional(),
            opvDose3: z.string().date().optional(),
            ipvOpvBooster: z.string().date().optional(),
            additionalIpvOrOpvInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryPneumococcalPolysaccharidePneumoniaVaccine: z
        .object({
            pneumoniaVaccineDose1: z.string().date().optional(),
            pneumoniaVaccineDose2: z.string().date().optional(),
            additionalPneumoniaInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryVaricellaChickenpox: z
        .object({
            chickenpoxDose1: z.string().date().optional(),
            chickenpoxDose2: z.string().date().optional(),
            iggAntibodyTiter: z.enum(['Positive', 'Negative', 'Not Performed']).optional(),
            haveYouEverHadACaseOfVaricellaChickenpox: z.enum(['Yes', 'No']).optional(),
            additionalChickenpoxInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryMeningococcalConjugateMcv4: z
        .object({
            mcv4Dose1: z.string().date().optional(),
            additionalMcv4Information: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryMeningococcalPolysaccharideMpsv4: z
        .object({
            mpsv4Dose1: z.string().date().optional(),
            mpsv4Dose2: z.string().date().optional(),
            additionalMpsv4Information: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryPertussis: z
        .object({
            pertussisDose1: z.string().date().optional(),
            pertussisDose2: z.string().date().optional(),
            pertussisDose3: z.string().date().optional(),
            pertussisDose4: z.string().date().optional(),
            pertussisDose5: z.string().date().optional(),
            additionalPertussisInformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
        })
        .optional(),
    medicalHistoryCovid19Vaccine: z
        .object({
            dateOfLastCovid19VaccineDose: z.string().date().optional(),
            additionalcovid19vaccineinformation: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
            covid19Card: z.any().optional(),
        })
        .optional(),
});

export const extendedAccountSchema = adjustZodSchemaDefaultForUnknownFields(
    personCredentialsSchema
        .merge(accountBackgroundCheckSchema)
        .merge(medicalHistorySchema)
        .merge(
            z.object({
                genderId: z.string().max(zodKnownFieldDefaultValidation.stringMax).optional(),
            }),
        ),
);
export type ExtendedAccountData = z.infer<typeof extendedAccountSchema>;
export type ExtendedAccountDef = EntityDef<ExtendedAccountData, ExtendedAccountColumns, AppState, UserState>;

export type ExtendedAccountColumns = AccountColumns &
    BackgroundCheckColumns &
    PersonCredentialsColumns &
    EntityColumns<typeof extendedColumns>;

export const extendedColumns = defineColumns({
    // Required for credentialing location filter
    stateOfWork: {
        type: 'string',
        index: true,
    },
});
